import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useParams, useSearchParams } from 'react-router-dom';
import Error404 from 'components/errors/Error404';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBarcode,
  faBoxOpen,
  faCheckCircle,
  faHashtag,
  faLayerGroup,
  faMapMarkerAlt,
  faSearchPlus,
  faTruck,
  faUser,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import JsBarcode from 'jsbarcode';
import dayjs from 'dayjs';
import FalconLightBox from 'components/common/FalconLightBox';
import Flex from 'components/common/Flex';
import { getFrameDespatchDetail } from 'services/smart-label/smartLabelService';
import emplasLogo from 'assets/img/Emplas/emplas-next-level-new.png';
import './SmartLabelLanding.css';

const DATE_FORMAT = 'DD MMM YYYY';
const DATETIME_FORMAT = 'DD MMM YYYY • HH:mm';

const isRealDate = value => {
  if (!value) return false;
  const parsed = dayjs(value);
  return parsed.isValid() && parsed.year() > 1901;
};

const formatDate = (value, pattern) =>
  isRealDate(value) ? dayjs(value).format(pattern) : '';

const parseFrameBarcode = barcode => {
  if (!barcode || typeof barcode !== 'string') return null;

  const raw = barcode.trim();
  if (raw.length < 13) return null;

  const headerId = parseInt(raw.substring(0, 9), 10);
  const frameId = parseInt(raw.substring(9, 13), 10);

  if (Number.isNaN(headerId) || Number.isNaN(frameId)) return null;

  return { headerId, frameId, raw };
};

const FrameImagePlaceholder = () => (
  <Flex
    direction="column"
    alignItems="center"
    justifyContent="center"
    className="sl-frame__placeholder"
  >
    <FontAwesomeIcon icon={faBoxOpen} className="sl-frame__placeholder-icon" />
    <p className="sl-frame__placeholder-text mb-0">Frame image unavailable</p>
  </Flex>
);

const FrameImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  if (!src || hasError) {
    return <FrameImagePlaceholder />;
  }

  return (
    <FalconLightBox image={src}>
      <div className="sl-frame__imagebox">
        <img
          src={src}
          alt={alt}
          className="sl-frame__image"
          onError={() => setHasError(true)}
        />
        <span className="sl-frame__zoom">
          <FontAwesomeIcon icon={faSearchPlus} />
        </span>
      </div>
    </FalconLightBox>
  );
};

FrameImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string
};

FrameImage.defaultProps = { alt: 'Frame' };

const DetailItem = ({ icon, variant, label, value, emptyText }) => (
  <div className="sl-detail">
    <span className={`sl-detail__icon bg-soft-${variant} text-${variant}`}>
      <FontAwesomeIcon icon={icon} />
    </span>
    <div className="sl-detail__body">
      <p className="sl-detail__label">{label}</p>
      <p className="sl-detail__value">{value || emptyText || '\u00A0'}</p>
    </div>
  </div>
);

DetailItem.propTypes = {
  icon: PropTypes.object.isRequired,
  variant: PropTypes.string,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  emptyText: PropTypes.string
};

DetailItem.defaultProps = { variant: 'primary', emptyText: 'Not available' };

const FrameBarcode = ({ value }) => {
  const svgRef = useRef(null);
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    if (!svgRef.current || !value) {
      setIsRendered(false);
      return;
    }
    try {
      JsBarcode(svgRef.current, value, {
        format: 'CODE128',
        displayValue: false,
        margin: 0,
        height: 56,
        width: 2,
        lineColor: '#0b1727',
        background: 'transparent'
      });
      setIsRendered(true);
    } catch (error) {
      setIsRendered(false);
    }
  }, [value]);

  return (
    <div className="sl-barcode">
      <svg
        ref={svgRef}
        className="sl-barcode__svg"
        style={{ display: isRendered ? 'block' : 'none' }}
      />
      <p className="sl-barcode__value mb-0">{value}</p>
    </div>
  );
};

FrameBarcode.propTypes = { value: PropTypes.string };

const MessageScreen = ({ icon, title, message, showSpinner }) => (
  <div className="sl-page">
    <header className="sl-topbar">
      <Container className="sl-topbar__inner">
        <img src={emplasLogo} alt="Emplas" className="sl-topbar__logo" />
        <span className="sl-topbar__tag">
          <FontAwesomeIcon icon={faBarcode} className="me-2" />
          Smart Label
        </span>
      </Container>
    </header>
    <div className="sl-message">
      {showSpinner ? (
        <Spinner animation="border" variant="primary" role="status" />
      ) : (
        <FontAwesomeIcon icon={icon} className="sl-message__icon" />
      )}
      {title && <h1 className="sl-message__title">{title}</h1>}
      {message && <p className="sl-message__text mb-0">{message}</p>}
    </div>
  </div>
);

MessageScreen.propTypes = {
  icon: PropTypes.object,
  title: PropTypes.string,
  message: PropTypes.string,
  showSpinner: PropTypes.bool
};

MessageScreen.defaultProps = { icon: faBarcode };

const SmartLabelLanding = () => {
  const { barcode: barcodeParam } = useParams();
  const [searchParams] = useSearchParams();
  const barcode = barcodeParam ?? searchParams.get('barcode');
  const isBarcodeLike = /^\d+$/.test(barcode || '');
  const parsed = isBarcodeLike ? parseFrameBarcode(barcode) : null;
  const headerId = parsed?.headerId;
  const frameId = parsed?.frameId;

  const [label, setLabel] = useState(null);
  const [loading, setLoading] = useState(Boolean(parsed));
  const controllerRef = useRef(null);

  useEffect(() => {
    if (headerId === undefined || frameId === undefined) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    controllerRef.current = new AbortController();

    getFrameDespatchDetail(headerId, frameId, controllerRef.current.signal)
      .then(res => {
        if (active) setLabel(res);
      })
      .catch(() => {
        if (active) setLabel(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controllerRef.current?.abort();
      controllerRef.current = null;
    };
  }, [headerId, frameId]);

  if (!isBarcodeLike) {
    return <Error404 />;
  }

  if (!parsed) {
    return (
      <MessageScreen
        title="Invalid label"
        message="This smart label could not be read. Please contact your installer."
      />
    );
  }

  if (loading) {
    return <MessageScreen showSpinner message="Loading your frame details…" />;
  }

  if (!label) {
    return (
      <MessageScreen
        icon={faBoxOpen}
        title="Frame not found"
        message="We couldn't find details for this label. Please contact your installer."
      />
    );
  }

  const deliveryDate = formatDate(label.deliveryDate, DATE_FORMAT);
  const qcCheckedAt = formatDate(label.qcCheckedAt, DATETIME_FORMAT);
  const barcodeValue = parsed.raw;
  const frameNo = String(parsed.frameId);

  return (
    <div className="sl-page">
      {/* Top bar */}
      <header className="sl-topbar">
        <Container className="sl-topbar__inner">
          <img src={emplasLogo} alt="Emplas" className="sl-topbar__logo" />
          <span className="sl-topbar__tag">
            <FontAwesomeIcon icon={faBarcode} className="me-2" />
            Smart Label
          </span>
        </Container>
      </header>

      <Container className="sl-container">
        <section className="sl-hero">
          <div className="sl-hero__glow" aria-hidden="true" />
          <div className="sl-hero__content">
            <p className="sl-hero__eyebrow">Your window frame</p>
            <h1 className="sl-hero__title">Job No. {label.jobNo || '—'}</h1>
            <p className="sl-hero__subtitle">
              	Frame No. {frameNo} · scanned from your smart label
            </p>
            <div className="sl-hero__status">
              <span className="sl-status sl-status--pass">
                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                Quality checked
              </span>
            </div>
          </div>
        </section>

        <Row className="g-4 sl-main">
          {/* Frame image */}
          <Col lg={7}>
            <Card className="sl-card h-100">
              <Card.Header className="sl-card__header">
                <h5 className="sl-card__title mb-0">Frame image</h5>
                <span className="sl-card__caption">Tap to enlarge</span>
              </Card.Header>
              <Card.Body className="sl-frame">
                <FrameImage
                  src={label.frameImageUrl}
                  alt={`Job No. ${label.jobNo} Frame No. ${frameNo}`}
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Details */}
          <Col lg={5}>
            <Card className="sl-card h-100">
              <Card.Header className="sl-card__header">
                <h5 className="sl-card__title mb-0">Frame details</h5>
              </Card.Header>
              <Card.Body className="sl-details">
                <DetailItem
                  icon={faHashtag}
                  variant="primary"
                  label="Job No"
                  value={label.jobNo}
                />
                <DetailItem
                  icon={faLayerGroup}
                  variant="info"
                  label="Frame No"
                  value={frameNo}
                />
                <DetailItem
                  icon={faMapMarkerAlt}
                  variant="info"
                  label="Location"
                  value={label.winNo ? `Win ${label.winNo}` : ''}
                />
                <DetailItem
                  icon={faUser}
                  variant="secondary"
                  label="Customer"
                  value={label.customerName}
                />
                <DetailItem
                  icon={faTruck}
                  variant="warning"
                  label="Delivery Date"
                  value={deliveryDate}
                />
                <DetailItem
                  icon={faUserCheck}
                  variant="success"
                  label="QC Check"
                  value={qcCheckedAt}
                  emptyText=""
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Barcode */}
        <Card className="sl-card sl-card--barcode">
          <Card.Body>
            <Flex
              justifyContent="between"
              alignItems="center"
              wrap="wrap"
              className="gap-3"
            >
              <div>
                <p className="sl-card__caption mb-1">Label reference</p>
                <h5 className="sl-card__title mb-0">
                  {label.jobNo || '—'} / Frame No. {frameNo}
                </h5>
              </div>
              <FrameBarcode value={barcodeValue} />
            </Flex>
          </Card.Body>
        </Card>

        {/* Footer */}
        <footer className="sl-footer">
          <img src={emplasLogo} alt="Emplas" className="sl-footer__logo" />
          <p className="sl-footer__text mb-0">
            Manufactured and quality checked by Emplas Window Products Ltd.
          </p>
          <p className="sl-footer__meta mb-0">
            Questions about this frame? Please contact your installer quoting
            job {label.jobNo || '—'}.
          </p>
        </footer>
      </Container>
    </div>
  );
};

export default SmartLabelLanding;
